/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Verdi Dashboard API
 * API behind the Verdi irrigation dashboard: organizations, their devices, zones and irrigation schedules, and the sensor data those devices report.
 *
 * ## Authentication
 *
 * Most operations need a Descope session token, sent as `Authorization: Bearer <token>`. Each operation's `security` lists what it accepts. Role and permission names there, such as `Verdi Administrator`, are Descope roles or permissions the session must carry, and the operation's description repeats them under **Access**. Operations with `security: []` are public.
 *
 * ## Organization context
 *
 * Data belongs to organizations. A user's session acts as their primary organization, or as another organization they belong to when its ID is sent in the `User` header. Administrators must send the `User` header on organization-scoped operations, or the request is rejected with a 400.
 *
 * ## Errors
 *
 * Error bodies are JSON `{ "message": string }`.
 *
 * - **400**: the path, query or body failed validation, or the request broke a domain rule.
 * - **401 / 403**: sent by the auth middleware; the body may be empty.
 * - **409**: see below.
 * - **500**: an unexpected server error.
 *
 * ## Versioned resources
 *
 * Versioned resources carry a `versionId`. Send the `versionId` you last read on a PATCH (in the body) or a DELETE (as a query parameter). If the resource changed since, the request gets a 409: reload it and retry. A 409 that includes a `code` is a conflict specific to that operation, described on the operation. On a create, 409 means the resource already exists.
 *
 * ## Reads with a request body
 *
 * Some reads take their input as a JSON body on the HTTP `QUERY` method; these operations are marked `x-read-only: true`. Deprecated POST and GET variants serve clients that cannot send `QUERY`.
 *
 * ## Identifiers
 *
 * MongoDB ObjectIds are 24-character hexadecimal strings.
 * OpenAPI spec version: 1.0.0
 */

import type {
	DataTag,
	DefinedInitialDataOptions,
	DefinedUseQueryResult,
	MutationFunction,
	QueryClient,
	QueryFunction,
	QueryKey,
	UndefinedInitialDataOptions,
	UseMutationOptions,
	UseMutationResult,
	UseQueryOptions,
	UseQueryResult,
} from "@tanstack/react-query";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { customFetch } from "../custom-fetch.ts";
import type {
	AreaOfInterest,
	CreateSite400,
	CreateSite422,
	CreateSite500,
	CreateSite503,
	CreateSiteBody,
	DeleteSite400,
	DeleteSite404,
	DeleteSite500,
	GetSite400,
	GetSite404,
	GetSite500,
	ListSites400,
	ListSites500,
	UpdateSite400,
	UpdateSite404,
	UpdateSite422,
	UpdateSite500,
	UpdateSite503,
	UpdateSiteBody,
} from "./schemas";

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(
	query: T,
	queryKey: K,
): T & { queryKey: K } => {
	const result = { queryKey } as T & { queryKey: K };
	for (const key of Object.keys(query)) {
		// The explicit queryKey always wins, matching the previous
		// `{ ...query, queryKey }` spread where it was set last.
		if (key === "queryKey") continue;
		Object.defineProperty(result, key, {
			enumerable: true,
			configurable: true,
			get: () => (query as Record<string, unknown>)[key],
		});
	}
	return result;
};

export const getGetSiteUrl = (siteId: string) => {
	return `/sites/${siteId}`;
};

/**
 * **Access:** Requires a user session token. Acts on one organization: administrators must name it in the `User` header.
 * @summary Get a single Area of Interest by ID
 */
export const getSite = async (
	siteId: string,
	options?: Parameters<typeof customFetch>[1],
): Promise<AreaOfInterest> => {
	return customFetch<AreaOfInterest>(getGetSiteUrl(siteId), {
		...options,
		method: "GET",
	});
};

export const getGetSiteQueryKey = (siteId: string) => {
	return ["sites", siteId] as const;
};

export const getGetSiteQueryOptions = <
	TData = Awaited<ReturnType<typeof getSite>>,
	TError = GetSite400 | void | GetSite404 | GetSite500,
>(
	siteId: string,
	options?: {
		query?: Partial<
			UseQueryOptions<Awaited<ReturnType<typeof getSite>>, TError, TData>
		>;
		request?: SecondParameter<typeof customFetch>;
	},
) => {
	const { query: queryOptions, request: requestOptions } = options ?? {};

	const queryKey = queryOptions?.queryKey ?? getGetSiteQueryKey(siteId);

	const queryFn: QueryFunction<Awaited<ReturnType<typeof getSite>>> = ({
		signal,
	}) => getSite(siteId, { signal, ...requestOptions });

	return {
		queryKey,
		queryFn,
		enabled: siteId !== null && siteId !== undefined,
		...queryOptions,
	} as UseQueryOptions<Awaited<ReturnType<typeof getSite>>, TError, TData> & {
		queryKey: DataTag<QueryKey, TData, TError>;
	};
};

export type GetSiteQueryResult = NonNullable<
	Awaited<ReturnType<typeof getSite>>
>;
export type GetSiteQueryError = GetSite400 | void | GetSite404 | GetSite500;

export function useGetSite<
	TData = Awaited<ReturnType<typeof getSite>>,
	TError = GetSite400 | void | GetSite404 | GetSite500,
>(
	siteId: string,
	options: {
		query: Partial<
			UseQueryOptions<Awaited<ReturnType<typeof getSite>>, TError, TData>
		> &
			Pick<
				DefinedInitialDataOptions<
					Awaited<ReturnType<typeof getSite>>,
					TError,
					Awaited<ReturnType<typeof getSite>>
				>,
				"initialData"
			>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetSite<
	TData = Awaited<ReturnType<typeof getSite>>,
	TError = GetSite400 | void | GetSite404 | GetSite500,
>(
	siteId: string,
	options?: {
		query?: Partial<
			UseQueryOptions<Awaited<ReturnType<typeof getSite>>, TError, TData>
		> &
			Pick<
				UndefinedInitialDataOptions<
					Awaited<ReturnType<typeof getSite>>,
					TError,
					Awaited<ReturnType<typeof getSite>>
				>,
				"initialData"
			>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetSite<
	TData = Awaited<ReturnType<typeof getSite>>,
	TError = GetSite400 | void | GetSite404 | GetSite500,
>(
	siteId: string,
	options?: {
		query?: Partial<
			UseQueryOptions<Awaited<ReturnType<typeof getSite>>, TError, TData>
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get a single Area of Interest by ID
 */

export function useGetSite<
	TData = Awaited<ReturnType<typeof getSite>>,
	TError = GetSite400 | void | GetSite404 | GetSite500,
>(
	siteId: string,
	options?: {
		query?: Partial<
			UseQueryOptions<Awaited<ReturnType<typeof getSite>>, TError, TData>
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
} {
	const queryOptions = getGetSiteQueryOptions(siteId, options);

	const query = useQuery(queryOptions, queryClient) as UseQueryResult<
		TData,
		TError
	> & { queryKey: DataTag<QueryKey, TData, TError> };

	return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get a single Area of Interest by ID
 */
export const useSetGetSiteQueryData = () => {
	const queryClient = useQueryClient();
	return (
		siteId: string,
		updater:
			| Awaited<ReturnType<typeof getSite>>
			| undefined
			| ((
					old: Awaited<ReturnType<typeof getSite>> | undefined,
			  ) => Awaited<ReturnType<typeof getSite>> | undefined),
		$exactMatch: boolean = true,
	) => {
		queryClient.setQueriesData<Awaited<ReturnType<typeof getSite>>>(
			{ exact: $exactMatch, queryKey: getGetSiteQueryKey(siteId) },
			updater,
		);
	};
};

/**
 * @summary Get a single Area of Interest by ID
 */
export const useGetGetSiteQueryData = () => {
	const queryClient = useQueryClient();
	return (siteId: string) =>
		queryClient.getQueryData<Awaited<ReturnType<typeof getSite>>>(
			getGetSiteQueryKey(siteId),
		);
};

export const getUpdateSiteUrl = (siteId: string) => {
	return `/sites/${siteId}`;
};

/**
 * **Access:** Requires a user session token. Acts on one organization: administrators must name it in the `User` header.
 * @summary Update an existing Area of Interest
 */
export const updateSite = async (
	siteId: string,
	updateSiteBody: UpdateSiteBody,
	options?: Parameters<typeof customFetch>[1],
): Promise<AreaOfInterest> => {
	const getHeaders = (
		h?: NonNullable<RequestInit["headers"]>,
	): Record<string, string | readonly string[]> => {
		if (!h) return {};
		if (h instanceof Headers) return Object.fromEntries(h.entries());
		if (Symbol.iterator in h) {
			return Object.fromEntries(
				Array.from(
					h as Iterable<Iterable<string>>,
					(entry) => Array.from(entry) as [string, string],
				),
			);
		}
		const headers: Record<string, string | readonly string[]> = {};
		for (const [name, value] of Object.entries<
			string | readonly string[] | undefined
		>(h)) {
			if (value !== undefined) headers[name] = value;
		}
		return headers;
	};
	return customFetch<AreaOfInterest>(getUpdateSiteUrl(siteId), {
		...options,
		method: "PATCH",
		headers: {
			"Content-Type": "application/json",
			...getHeaders(options?.headers),
		},
		body: JSON.stringify(updateSiteBody),
	});
};

export const getUpdateSiteMutationKey = () => ["updateSite"] as const;

export const getUpdateSiteMutationOptions = <
	TError =
		| UpdateSite400
		| void
		| UpdateSite404
		| UpdateSite422
		| UpdateSite500
		| UpdateSite503,
	TContext = unknown,
>(options?: {
	mutation?: UseMutationOptions<
		Awaited<ReturnType<typeof updateSite>>,
		TError,
		UpdateSiteMutationVariables,
		TContext
	>;
	request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
	Awaited<ReturnType<typeof updateSite>>,
	TError,
	UpdateSiteMutationVariables,
	TContext
> => {
	const mutationKey = getUpdateSiteMutationKey();
	const { mutation: mutationOptions, request: requestOptions } = options
		? options.mutation &&
			"mutationKey" in options.mutation &&
			options.mutation.mutationKey
			? options
			: { ...options, mutation: { ...options.mutation, mutationKey } }
		: { mutation: { mutationKey }, request: undefined };

	const mutationFn: MutationFunction<
		Awaited<ReturnType<typeof updateSite>>,
		UpdateSiteMutationVariables
	> = (props) => {
		const { siteId, data } = props ?? {};

		return updateSite(siteId, data, requestOptions);
	};

	return { mutationFn, ...mutationOptions };
};

export type UpdateSiteMutationResult = NonNullable<
	Awaited<ReturnType<typeof updateSite>>
>;
export type UpdateSiteMutationBody = UpdateSiteBody;
export type UpdateSiteMutationError =
	| UpdateSite400
	| void
	| UpdateSite404
	| UpdateSite422
	| UpdateSite500
	| UpdateSite503;
export type UpdateSiteMutationVariables = {
	siteId: string;
	data: UpdateSiteBody;
};

/**
 * @summary Update an existing Area of Interest
 */
export const useUpdateSite = <
	TError =
		| UpdateSite400
		| void
		| UpdateSite404
		| UpdateSite422
		| UpdateSite500
		| UpdateSite503,
	TContext = unknown,
>(
	options?: {
		mutation?: UseMutationOptions<
			Awaited<ReturnType<typeof updateSite>>,
			TError,
			UpdateSiteMutationVariables,
			TContext
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseMutationResult<
	Awaited<ReturnType<typeof updateSite>>,
	TError,
	UpdateSiteMutationVariables,
	TContext
> => {
	return useMutation(getUpdateSiteMutationOptions(options), queryClient);
};
export const getDeleteSiteUrl = (siteId: string) => {
	return `/sites/${siteId}`;
};

/**
 * **Access:** Requires a user session token. Acts on one organization: administrators must name it in the `User` header.
 * @summary Delete an existing Area of Interest
 */
export const deleteSite = async (
	siteId: string,
	options?: Parameters<typeof customFetch>[1],
): Promise<AreaOfInterest> => {
	return customFetch<AreaOfInterest>(getDeleteSiteUrl(siteId), {
		...options,
		method: "DELETE",
	});
};

export const getDeleteSiteMutationKey = () => ["deleteSite"] as const;

export const getDeleteSiteMutationOptions = <
	TError = DeleteSite400 | void | DeleteSite404 | DeleteSite500,
	TContext = unknown,
>(options?: {
	mutation?: UseMutationOptions<
		Awaited<ReturnType<typeof deleteSite>>,
		TError,
		DeleteSiteMutationVariables,
		TContext
	>;
	request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
	Awaited<ReturnType<typeof deleteSite>>,
	TError,
	DeleteSiteMutationVariables,
	TContext
> => {
	const mutationKey = getDeleteSiteMutationKey();
	const { mutation: mutationOptions, request: requestOptions } = options
		? options.mutation &&
			"mutationKey" in options.mutation &&
			options.mutation.mutationKey
			? options
			: { ...options, mutation: { ...options.mutation, mutationKey } }
		: { mutation: { mutationKey }, request: undefined };

	const mutationFn: MutationFunction<
		Awaited<ReturnType<typeof deleteSite>>,
		DeleteSiteMutationVariables
	> = (props) => {
		const { siteId } = props ?? {};

		return deleteSite(siteId, requestOptions);
	};

	return { mutationFn, ...mutationOptions };
};

export type DeleteSiteMutationResult = NonNullable<
	Awaited<ReturnType<typeof deleteSite>>
>;

export type DeleteSiteMutationError =
	| DeleteSite400
	| void
	| DeleteSite404
	| DeleteSite500;
export type DeleteSiteMutationVariables = { siteId: string };

/**
 * @summary Delete an existing Area of Interest
 */
export const useDeleteSite = <
	TError = DeleteSite400 | void | DeleteSite404 | DeleteSite500,
	TContext = unknown,
>(
	options?: {
		mutation?: UseMutationOptions<
			Awaited<ReturnType<typeof deleteSite>>,
			TError,
			DeleteSiteMutationVariables,
			TContext
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseMutationResult<
	Awaited<ReturnType<typeof deleteSite>>,
	TError,
	DeleteSiteMutationVariables,
	TContext
> => {
	return useMutation(getDeleteSiteMutationOptions(options), queryClient);
};
export const getListSitesUrl = () => {
	return `/sites`;
};

/**
 * **Access:** Requires a user session token. Acts on one organization: administrators must name it in the `User` header.
 * @summary List all Areas of Interest
 */
export const listSites = async (
	options?: Parameters<typeof customFetch>[1],
): Promise<AreaOfInterest[]> => {
	return customFetch<AreaOfInterest[]>(getListSitesUrl(), {
		...options,
		method: "GET",
	});
};

export const getListSitesQueryKey = () => {
	return ["sites"] as const;
};

export const getListSitesQueryOptions = <
	TData = Awaited<ReturnType<typeof listSites>>,
	TError = ListSites400 | void | ListSites500,
>(options?: {
	query?: Partial<
		UseQueryOptions<Awaited<ReturnType<typeof listSites>>, TError, TData>
	>;
	request?: SecondParameter<typeof customFetch>;
}) => {
	const { query: queryOptions, request: requestOptions } = options ?? {};

	const queryKey = queryOptions?.queryKey ?? getListSitesQueryKey();

	const queryFn: QueryFunction<Awaited<ReturnType<typeof listSites>>> = ({
		signal,
	}) => listSites({ signal, ...requestOptions });

	return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
		Awaited<ReturnType<typeof listSites>>,
		TError,
		TData
	> & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListSitesQueryResult = NonNullable<
	Awaited<ReturnType<typeof listSites>>
>;
export type ListSitesQueryError = ListSites400 | void | ListSites500;

export function useListSites<
	TData = Awaited<ReturnType<typeof listSites>>,
	TError = ListSites400 | void | ListSites500,
>(
	options: {
		query: Partial<
			UseQueryOptions<Awaited<ReturnType<typeof listSites>>, TError, TData>
		> &
			Pick<
				DefinedInitialDataOptions<
					Awaited<ReturnType<typeof listSites>>,
					TError,
					Awaited<ReturnType<typeof listSites>>
				>,
				"initialData"
			>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListSites<
	TData = Awaited<ReturnType<typeof listSites>>,
	TError = ListSites400 | void | ListSites500,
>(
	options?: {
		query?: Partial<
			UseQueryOptions<Awaited<ReturnType<typeof listSites>>, TError, TData>
		> &
			Pick<
				UndefinedInitialDataOptions<
					Awaited<ReturnType<typeof listSites>>,
					TError,
					Awaited<ReturnType<typeof listSites>>
				>,
				"initialData"
			>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListSites<
	TData = Awaited<ReturnType<typeof listSites>>,
	TError = ListSites400 | void | ListSites500,
>(
	options?: {
		query?: Partial<
			UseQueryOptions<Awaited<ReturnType<typeof listSites>>, TError, TData>
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary List all Areas of Interest
 */

export function useListSites<
	TData = Awaited<ReturnType<typeof listSites>>,
	TError = ListSites400 | void | ListSites500,
>(
	options?: {
		query?: Partial<
			UseQueryOptions<Awaited<ReturnType<typeof listSites>>, TError, TData>
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
} {
	const queryOptions = getListSitesQueryOptions(options);

	const query = useQuery(queryOptions, queryClient) as UseQueryResult<
		TData,
		TError
	> & { queryKey: DataTag<QueryKey, TData, TError> };

	return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List all Areas of Interest
 */
export const useSetListSitesQueryData = () => {
	const queryClient = useQueryClient();
	return (
		updater:
			| Awaited<ReturnType<typeof listSites>>
			| undefined
			| ((
					old: Awaited<ReturnType<typeof listSites>> | undefined,
			  ) => Awaited<ReturnType<typeof listSites>> | undefined),
		$exactMatch: boolean = true,
	) => {
		queryClient.setQueriesData<Awaited<ReturnType<typeof listSites>>>(
			{ exact: $exactMatch, queryKey: getListSitesQueryKey() },
			updater,
		);
	};
};

/**
 * @summary List all Areas of Interest
 */
export const useGetListSitesQueryData = () => {
	const queryClient = useQueryClient();
	return () =>
		queryClient.getQueryData<Awaited<ReturnType<typeof listSites>>>(
			getListSitesQueryKey(),
		);
};

export const getCreateSiteUrl = () => {
	return `/sites`;
};

/**
 * **Access:** Requires a user session token. Acts on one organization: administrators must name it in the `User` header.
 * @summary Create a new Area of Interest
 */
export const createSite = async (
	createSiteBody: CreateSiteBody,
	options?: Parameters<typeof customFetch>[1],
): Promise<AreaOfInterest> => {
	const getHeaders = (
		h?: NonNullable<RequestInit["headers"]>,
	): Record<string, string | readonly string[]> => {
		if (!h) return {};
		if (h instanceof Headers) return Object.fromEntries(h.entries());
		if (Symbol.iterator in h) {
			return Object.fromEntries(
				Array.from(
					h as Iterable<Iterable<string>>,
					(entry) => Array.from(entry) as [string, string],
				),
			);
		}
		const headers: Record<string, string | readonly string[]> = {};
		for (const [name, value] of Object.entries<
			string | readonly string[] | undefined
		>(h)) {
			if (value !== undefined) headers[name] = value;
		}
		return headers;
	};
	return customFetch<AreaOfInterest>(getCreateSiteUrl(), {
		...options,
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			...getHeaders(options?.headers),
		},
		body: JSON.stringify(createSiteBody),
	});
};

export const getCreateSiteMutationKey = () => ["createSite"] as const;

export const getCreateSiteMutationOptions = <
	TError = CreateSite400 | void | CreateSite422 | CreateSite500 | CreateSite503,
	TContext = unknown,
>(options?: {
	mutation?: UseMutationOptions<
		Awaited<ReturnType<typeof createSite>>,
		TError,
		CreateSiteMutationVariables,
		TContext
	>;
	request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
	Awaited<ReturnType<typeof createSite>>,
	TError,
	CreateSiteMutationVariables,
	TContext
> => {
	const mutationKey = getCreateSiteMutationKey();
	const { mutation: mutationOptions, request: requestOptions } = options
		? options.mutation &&
			"mutationKey" in options.mutation &&
			options.mutation.mutationKey
			? options
			: { ...options, mutation: { ...options.mutation, mutationKey } }
		: { mutation: { mutationKey }, request: undefined };

	const mutationFn: MutationFunction<
		Awaited<ReturnType<typeof createSite>>,
		CreateSiteMutationVariables
	> = (props) => {
		const { data } = props ?? {};

		return createSite(data, requestOptions);
	};

	return { mutationFn, ...mutationOptions };
};

export type CreateSiteMutationResult = NonNullable<
	Awaited<ReturnType<typeof createSite>>
>;
export type CreateSiteMutationBody = CreateSiteBody;
export type CreateSiteMutationError =
	| CreateSite400
	| void
	| CreateSite422
	| CreateSite500
	| CreateSite503;
export type CreateSiteMutationVariables = { data: CreateSiteBody };

/**
 * @summary Create a new Area of Interest
 */
export const useCreateSite = <
	TError = CreateSite400 | void | CreateSite422 | CreateSite500 | CreateSite503,
	TContext = unknown,
>(
	options?: {
		mutation?: UseMutationOptions<
			Awaited<ReturnType<typeof createSite>>,
			TError,
			CreateSiteMutationVariables,
			TContext
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseMutationResult<
	Awaited<ReturnType<typeof createSite>>,
	TError,
	CreateSiteMutationVariables,
	TContext
> => {
	return useMutation(getCreateSiteMutationOptions(options), queryClient);
};
